<script lang="ts" setup>
import type { EditorialCarouselProductsWithChipsProps } from '@design-system/components/Editorial/EditorialCarouselProductsWithChips.props'
import type { ProductTileProps } from '@design-system/components/UI/UIProductTile.props'
import type { EditorialCarouselProductWithChips } from '@integration-layer/ampliance/schemas/editorial-carousel-product-with-chips-schema.localized'
import type { ProductCardHorizontalProps } from '@design-system/components/UI/UIProductCardHorizontal.props'
import type { CarouselChipContent } from '@design-system/components/UI/CarouselChips/UICarouselChips.props'

type ProductCardHorizontal = ProductCardHorizontalProps

const props = defineProps<{
  deliveryId: string
  locale: string
  vse?: string
  content?: EditorialCarouselProductWithChips
}>()

const data =
  await useComponentResponse<EditorialCarouselProductWithChips>(props)

const { getProducts } = useShopTheLook()

const { loadAlgoliaProducts } = useAlgoliaProducts()

const { dispatchAddToCartEvent } = useGACartEvents()

const { normalizeProduct, transformToProductCardHorizontal } =
  useWrapperProductTile()

// Open quick buy
const { openQuickBuySlide } = await useUIQuickBuyPopUpSlide()

const openQuickBuyPopUpLayer = (product: ProductCardHorizontal) => {
  openQuickBuySlide(
    {
      product: product,
      isQuickBuyPlp: true,
    },
    {
      saveChanges: () => {
        dispatchAddToCartEvent({ view: 'quick_product_popup' })
      },
    }
  )
}

const addToCart = async (productToLoad: string) => {
  const loadedProduct = await loadAlgoliaProducts([productToLoad])
  const normalizedProduct = normalizeProduct(loadedProduct[productToLoad])

  if (normalizedProduct) {
    const transformedData = transformToProductCardHorizontal(normalizedProduct)
    openQuickBuyPopUpLayer(transformedData)
  }
}

const selectedChip = ref(
  data.value?.carouselProducts?.content?.[0] as unknown as CarouselChipContent
)

const products = computed(() => selectedChip?.value.products ?? [])

const xGenId = computed(() => selectedChip?.value.xGen)

const viewAllCtaLink = computed(() =>
  selectedChip.value?.viewAllCtaLink
    ? {
        target: selectedChip.value.viewAllCtaLink.target,
        type: selectedChip.value.viewAllCtaLink.type,
      }
    : null
)

const response: Record<string, ProductTileProps[]> = {}

const { data: selectedProducts, status: selectedProductsStatus } =
  await useAsyncData<ProductTileProps[]>(
    `carouselProducts-${props.deliveryId}`,
    async () => {
      const key = xGenId.value ?? products.value.join(',')

      if (response[key]) {
        return response[key]
      }

      const fetchResponse = await getProducts(xGenId.value || products.value)

      response[key] = fetchResponse

      return fetchResponse
    },
    {
      watch: [products, xGenId],
      immediate: true,
      server: false,
    }
  )

const handleChipSelected = async (content: CarouselChipContent) => {
  selectedChip.value = content
}
</script>

<template>
  <EditorialCarouselProductsWithChips
    :handle-chip-selected="handleChipSelected"
    :selected-products-status="selectedProductsStatus"
    :view-all-cta-link="viewAllCtaLink"
    :selected-products="selectedProducts as ProductTileProps[]"
    v-bind="data as unknown as EditorialCarouselProductsWithChipsProps"
    @add-to-cart="
      (tileProduct: ProductTileProps) => addToCart(tileProduct.product.sku)
    "
  />
</template>
